import * as Sentry from '@sentry/react';
import React, { useEffect, useRef } from 'react';
import { Outlet, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { TDR } from 'tdr-common';
import { useCheckoutSession } from '../api/getCheckoutSession';
import { ErrorResponse } from '../common/ErrorResponse';
import { CheckoutURLParams } from '../common/types';
import Spinner from '../components/Spinner';
import { CheckoutProvider } from '../context/new/CheckoutContext';
import { analytics, checkoutMiddlewareBuilder } from '../helpers/analytics';
import TablzLogo from '../images/poweredBy/tablz.svg';
import { ResultPageLayout } from '../layouts/ResultPageLayout';
import { NotFoundErrorPage } from '../pages/errors/NotFoundErrorPage';
import { UnexpectedErrorPage } from '../pages/errors/UnexpectedErrorPage';
import { ValidationErrorPage } from '../pages/errors/ValidationErrorPage';
import { RestaurantGuardOutletContext } from './RestaurantGuard';
import { CheckoutLayout } from '../layouts/CheckoutLayout';
import { baseCheckoutNavConfig } from '../components/checkout/steps/config';
import { getEcommerceCheckoutNavConfig } from '../components/checkout/steps/ecommerceNavConfigHelper';
import { useTranslation } from 'react-i18next';
import type { MiddlewareFunction } from '../components/consent-modal/types';
import { emitBeginCheckout } from '../helpers/gtm/surface/events';
import { toISO4217 } from '../helpers/currency';
import { toLuxon } from 'tdr-common';

export interface CheckoutGuardOutletContext extends RestaurantGuardOutletContext {
	checkoutSession: TDR.CheckoutSession.CheckoutSession;
	invoice: TDR.Invoice;
	timeslot?: TDR.TimeSlot;
	guestDetails?: Pick<TDR.Guest.Guest, 'firstName' | 'lastName' | 'email' | 'phone' | 'hasFastCheckoutEnabled'>;
	ecommerceSections: TDR.Ecommerce.EcommerceSection[];
}

export const CheckoutGuard = () => {
	const { i18n } = useTranslation();
	const parentContext = useOutletContext<RestaurantGuardOutletContext>();
	const restaurant = parentContext.restaurant;
	const { checkoutId } = useParams<CheckoutURLParams>();
	const { data, isLoading, isError, error } = useCheckoutSession(checkoutId);
	const navigate = useNavigate();
	const lastBeginCheckoutIdRef = useRef<string | null>(null);
	const checkoutMiddlewareRef = useRef<{ checkoutId: string; middleware: MiddlewareFunction } | null>(null);

	// ALL HOOKS MUST BE CALLED BEFORE ANY CONDITIONAL RETURNS
	// Analytics tracking effect
	useEffect(() => {
		if (!data?.checkoutSession?.id) {
			return;
		}

		const currentCheckoutId = data.checkoutSession.id;

		// Ensure checkout middleware is only added once per checkout ID
		const existingMiddleware = checkoutMiddlewareRef.current;
		if (!existingMiddleware || existingMiddleware.checkoutId !== currentCheckoutId) {
			if (existingMiddleware) {
				analytics.removeSourceMiddleware(existingMiddleware.middleware);
			}
			const trackingMiddleware = checkoutMiddlewareBuilder(currentCheckoutId);
			analytics.addSourceMiddleware(trackingMiddleware);
			checkoutMiddlewareRef.current = { checkoutId: currentCheckoutId, middleware: trackingMiddleware };
		}

		// Track begin_checkout once per checkout ID to avoid double-fires
		// when /checkout/:id redirects to the first step (e.g. /policies).
		if (lastBeginCheckoutIdRef.current !== currentCheckoutId) {
			const r = restaurant;
			const luxonTime = toLuxon(data.checkoutSession.time, r.timezone);
			emitBeginCheckout(
				{
					venueId: r.id,
					venueName: r.name,
					currency: toISO4217(r.currency) ?? ''
				},
				{
					checkoutId: currentCheckoutId,
					guests: data.checkoutSession.guests,
					date: luxonTime.toFormat('yyyy-LL-dd'),
					time: luxonTime.toFormat('HH:mm'),
					items: (data.checkoutSession.items || []).map((item: any) => ({
						id: item.id || item.tableId || 'unknown',
						name: item.name || item.tableName || 'Table',
						price: item.price || 0,
						quantity: 1
					})),
					value: data.checkoutInvoice.total
				}
			);
			lastBeginCheckoutIdRef.current = currentCheckoutId;
		}

		return () => {
			const current = checkoutMiddlewareRef.current;
			if (current && current.checkoutId === currentCheckoutId) {
				analytics.removeSourceMiddleware(current.middleware);
				checkoutMiddlewareRef.current = null;
			}
		};
	}, [
		data?.checkoutSession?.id,
		data?.checkoutInvoice?.total,
		restaurant.id,
		restaurant.name,
		restaurant.currency
	]);

	// Navigate to confirmation step if checkout session is already completed
	useEffect(() => {
		if (data && data.checkoutSession?.status === 'completed') {
			navigate(`/${parentContext.restaurant.slug}/checkout/${data.checkoutSession.id}/confirmation`);
		}
	}, [data, navigate, parentContext.restaurant.slug]);

	// Handle initial navigation to first step for ecommerce sections
	useEffect(() => {
		if (data?.checkoutSession) {
			const currentPath = window.location.pathname;
			const baseCheckoutPath = `/${parentContext.restaurant.slug}/checkout/${data?.checkoutSession?.id}`;

			if (currentPath === baseCheckoutPath || currentPath === `${baseCheckoutPath}/`) {
				// Determine the first step based on checkout ordering
				let firstStep = 'policies'; // default fallback

				if (data?.ecommerceSections && data.ecommerceSections.length > 0) {
					// Use the ecommerce nav config to determine first step
					const ecommerceNavConfig = getEcommerceCheckoutNavConfig(data.ecommerceSections);
					const orderedSteps = Object.values(ecommerceNavConfig)
						.sort((a, b) => a.order - b.order);
					firstStep = orderedSteps[0]?.path || 'policies';
				}

				navigate(`${baseCheckoutPath}/${firstStep}`, { replace: true });
			}
		}
	}, [data, navigate, parentContext.restaurant.slug]);

	// Determine nav config based on available data
	let navConfig = baseCheckoutNavConfig;
	if (data?.ecommerceSections && data.ecommerceSections.length > 0) {
		navConfig = getEcommerceCheckoutNavConfig(data.ecommerceSections);
	}

	// NOW HANDLE LOADING AND ERROR STATES AFTER ALL HOOKS
	if (isLoading) {
		return <CheckoutLoadingStep key={i18n.language} />;
	}

	if (!isLoading) {
		if (error && error instanceof Response) {
			if (error.status === 404) {
				return <NotFoundErrorPage />;
			}
		}
		else if (error && error instanceof ErrorResponse) {
			if (error.response.status === 404) {
				return <NotFoundErrorPage />;
			}
			else if (error.response.status === 400 && error.body) {
				return <ValidationErrorPage to='..' error={error} />;
			}
		}
	}

	if (!isLoading && isError) {
		const eventID = Sentry.captureException(error);
		return <UnexpectedErrorPage to='..' sentryErrorID={eventID} />;
	}

	return (
		<CheckoutProvider session={data?.checkoutSession} navConfig={navConfig}>
			{data ? (
				<CheckoutLayout session={data?.checkoutSession}>
					<Outlet
						context={
              {
              	...parentContext,
              	checkoutSession: data?.checkoutSession,
              	invoice: data?.checkoutInvoice,
              	timeslot: data?.timeslot,
              	guestDetails: data?.guestDetails,
              	ecommerceSections: data?.ecommerceSections || []
              } satisfies CheckoutGuardOutletContext
						}
					/>
				</CheckoutLayout>
			) : (
				<CheckoutLoadingStep key={i18n.language} />
			)}
		</CheckoutProvider>
	);
};

export const CheckoutLoadingStep = () => {
	const { t, i18n } = useTranslation();

	return (
		<ResultPageLayout>
			<img src={TablzLogo} alt='Tablz logo' />
			<ResultPageLayout.Title key={i18n.language}>{t('messages.loading_booking_details')}</ResultPageLayout.Title>
			<ResultPageLayout.Body>
				<Spinner size='xs' />
			</ResultPageLayout.Body>
		</ResultPageLayout>
	);
};
